/**
 * CheckoutReturnBanner — dismissible post-checkout info banner for ParentDashboardPage.
 *
 * Implements US-013 / T2 (AC-1).
 *
 * Shown after the parent returns from Stripe checkout (when ParentDashboardPage detects
 * the ?checkout=success query marker). Communicates that the Otter Coin balance update
 * is in progress (async webhook lag — TS-008/TS-009) so the parent is not confused by
 * a briefly stale balance.
 *
 * Design: reuses the dismissible-banner markup pattern established in CoinBalancePanels
 * (WelcomeCreditBanner) — Card shell + amber palette + 44px dismiss touch target.
 * References: frontend/src/components/CoinBalancePanels.tsx lines 103–141.
 *
 * KB lessons enforced:
 *   TO-LL-007: verified no child component already renders an equivalent notice.
 *              The welcome banner (CoinBalancePanels/WelcomeCreditBanner) is a distinct,
 *              separate message; this banner does NOT duplicate it.
 *
 * CCM-03 compliance: this component renders only inside ParentDashboardPage, which
 *   mounts exclusively under ParentGuard. Students are redirected before this renders.
 *
 * Stateless presentation component — no timers, no data fetching, no query reading.
 * Visibility and timers are owned by ParentDashboardPage (T3).
 * The onDismiss callback is required; callers MUST supply it (enforced by TypeScript).
 */

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface CheckoutReturnBannerProps {
  /** Called when the parent clicks the dismiss (X) button. Required. */
  onDismiss: () => void;
}

// ─── CheckoutReturnBanner ────────────────────────────────────────────────────

/**
 * CheckoutReturnBanner — informs the parent that their Otter Coin balance will
 * update shortly after checkout, preventing confusion while the Stripe webhook
 * (TS-008/TS-009) processes the payment asynchronously.
 *
 * Implements US-013 / T2 / AC-1.
 *
 * Markup pattern copied from WelcomeCreditBanner in CoinBalancePanels.tsx:
 *   - Card className 'border-amber-200 bg-amber-50 mb-4' (amber palette)
 *   - role='status' aria-live='polite' (polite — non-urgent success notice)
 *   - Flex row: message block left, dismiss button right
 *   - Dismiss Button: h-11 (44px touch target), amber ghost style, aria-label
 *
 * KB TO-LL-007: no duplicate banner — welcome banner is a distinct message on a
 * separate code path in CoinBalancePanels; no collision possible.
 */
export function CheckoutReturnBanner({ onDismiss }: CheckoutReturnBannerProps) {
  return (
    <Card
      className="border-amber-200 bg-amber-50 mb-4"
      role="status"
      aria-live="polite"
    >
      <CardContent className="pt-4 pb-4">
        <div className="flex items-start justify-between gap-3">
          {/* Message block */}
          <div>
            <p className="text-sm font-semibold text-amber-800 mb-1">
              Payment successful!
            </p>
            <p className="text-sm text-amber-700">
              Your Otter Coin balance will update within a minute. Refresh to see your new balance.
            </p>
          </div>

          {/* Dismiss button — 44px touch target per UI/UX framework (mobile-first) */}
          <Button
            variant="ghost"
            size="sm"
            onClick={onDismiss}
            className="h-11 px-3 flex-shrink-0 text-amber-700 hover:text-amber-900 hover:bg-amber-100"
            aria-label="Dismiss notice"
          >
            ✕
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
